/**
 * Checklists page — find current and historical move-in / move-out checklists across leases
 * (BR-34). Table on laptop, cards on mobile; `View checklist` opens the read-only tenant modal.
 * Filter state lives in the URL query string. Route: /checklists
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState, FilterBar, PageHeader } from '../components/layout';
import { ChecklistStatusBadge } from '../components/condition/ChecklistStatusBadge';
import { TenantChecklistModal } from '../components/condition/TenantChecklistModal';
import { useChecklists } from '../lib/useChecklists';
import type { ChecklistRow, WalkthroughPhase } from '../lib/condition.api';
import { cn } from '@/lib/utils';
import { realUnitNumber } from '@resida/shared';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'NOT_STARTED', label: 'Not started' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'SUBMITTED', label: 'Submitted' },
] as const;
const PHASE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'MOVE_IN', label: 'Move-in' },
  { value: 'MOVE_OUT', label: 'Move-out' },
] as const;

type StatusValue = (typeof STATUS_OPTIONS)[number]['value'];
type PhaseValue = (typeof PHASE_OPTIONS)[number]['value'];

const SELECT_CLASS =
  'h-11 rounded-md border border-input md:h-9 bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const phaseLabel = (phase: WalkthroughPhase) => (phase === 'MOVE_IN' ? 'Move-in' : 'Move-out');
const rowKey = (row: ChecklistRow) => `${row.leaseId}-${row.phase === 'MOVE_IN' ? 'move-in' : 'move-out'}`;

function parseOption<T extends string>(raw: string | null, options: readonly { value: T }[]): T {
  return (options.find((o) => o.value === raw)?.value ?? '') as T;
}

export function ChecklistsPage() {
  const [params, setParams] = useSearchParams();
  const status = parseOption<StatusValue>(params.get('status'), STATUS_OPTIONS);
  const phase = parseOption<PhaseValue>(params.get('phase'), PHASE_OPTIONS);
  const leaseState = params.get('leaseState') === 'historical' ? 'historical' : 'current';
  const q = params.get('q') ?? '';

  const [search, setSearch] = useState(q);
  const [viewing, setViewing] = useState<{ walkthroughId: string; phase: WalkthroughPhase } | null>(null);

  const update = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  // Debounce the free-text search into the URL.
  useEffect(() => {
    if (search === q) return;
    const t = setTimeout(() => update('q', search.trim()), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const checklists = useChecklists({
    leaseState,
    ...(q ? { q } : {}),
    ...(status ? { status } : {}),
    ...(phase ? { phase } : {}),
  });

  const rows = checklists.data ?? [];

  const viewButton = (row: ChecklistRow, className?: string) =>
    row.tenant.state === 'SUBMITTED' && row.tenant.walkthroughId ? (
      <Button
        type="button"
        variant="outline"
        className={cn('text-foreground', className)}
        onClick={() => setViewing({ walkthroughId: row.tenant.walkthroughId as string, phase: row.phase })}
        data-testid={`checklist-view-${rowKey(row)}`}
      >
        View checklist
      </Button>
    ) : null;

  const tenantBadge = (row: ChecklistRow) => (
    <ChecklistStatusBadge
      state={row.tenant.state}
      submittedAt={row.tenant.submittedAt}
      optional={row.tenant.optional}
      shortDate
      data-testid={`checklist-state-${rowKey(row)}`}
    />
  );

  return (
    <div className="mx-auto max-w-6xl pb-6" data-testid="checklists-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader title="Checklists" subtitle="Move-in and move-out walkthroughs across your leases" subtitleWrap />

        <div data-testid="checklists-search">
          <FilterBar search={{ placeholder: 'Unit or property', value: search, onChange: setSearch }} />
        </div>
        {/* Filters sit outside FilterBar's `filters` slot: it renders that slot twice (mobile + desktop),
            which would duplicate the ids/test ids. One scrollable row serves both widths. */}
        <div className="mb-4 mt-2 flex items-center gap-2 overflow-x-auto scrollbar-none">
          <label className="sr-only" htmlFor="checklists-status-filter">Status</label>
          <select
            id="checklists-status-filter"
            className={SELECT_CLASS}
            value={status}
            onChange={(e) => update('status', e.target.value)}
            data-testid="checklists-status-filter"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.value ? o.label : 'All statuses'}</option>
            ))}
          </select>
          <label className="sr-only" htmlFor="checklists-phase-filter">Phase</label>
          <select
            id="checklists-phase-filter"
            className={SELECT_CLASS}
            value={phase}
            onChange={(e) => update('phase', e.target.value)}
            data-testid="checklists-phase-filter"
          >
            {PHASE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.value ? o.label : 'All phases'}</option>
            ))}
          </select>
          <div role="group" aria-label="Leases" className="order-first inline-flex shrink-0 items-center gap-2 md:order-last">
            <span className="text-sm text-muted-foreground" aria-hidden="true">Leases</span>
            <div className="inline-flex rounded-md border border-input p-0.5">
            {(['current', 'historical'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={leaseState === value}
                onClick={() => update('leaseState', value === 'current' ? '' : value)}
                data-testid={`checklists-lease-state-${value}`}
                className={cn(
                  'min-h-[44px] rounded px-3 md:min-h-[32px] text-sm font-medium transition-colors',
                  leaseState === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent',
                )}
              >
                {value === 'current' ? 'Current' : 'Historical'}
              </button>
            ))}
            </div>
          </div>
        </div>
      </div>

      <div className="px-[var(--page-padding-x)]">
        {checklists.status === 'loading' && (
          <div className="flex flex-col gap-2" role="status" aria-label="Loading checklists" data-testid="checklists-loading">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        )}

        {checklists.status === 'error' && (
          <div role="alert" className="flex flex-col items-start gap-2 rounded-xl border border-border bg-card p-[var(--card-padding)]" data-testid="checklists-error">
            <ChecklistStatusBadge state="ERROR" />
            <Button type="button" variant="ghost" className="px-1 text-foreground" onClick={checklists.retry} data-testid="checklist-retry">
              Retry
            </Button>
          </div>
        )}

        {checklists.status === 'ready' && checklists.truncated && (
          <p role="status" className="mb-3 text-sm text-muted-foreground" data-testid="checklists-truncated">
            Showing results from a limited number of leases. Narrow the search by unit or property to see others.
          </p>
        )}

        {checklists.status === 'ready' && rows.length === 0 && (
          <EmptyState
            icon={ClipboardCheck}
            headingLevel="h2"
            title={leaseState === 'historical' ? 'No historical checklists' : 'No checklists found'}
            description={
              q || status || phase
                ? 'Try changing or clearing the filters.'
                : 'Move-in and move-out checklists appear here as leases start and end.'
            }
            data-testid="checklists-empty"
          />
        )}

        {checklists.status === 'ready' && rows.length > 0 && (
          <>
            {/* Laptop: table */}
            <div className="hidden lg:block rounded-xl border border-border bg-card" role="table" aria-label="Checklists" data-testid="checklists-table">
              <div role="row" className="grid grid-cols-[1.5fr_0.8fr_1fr_1.4fr_auto] gap-4 border-b border-border px-4 py-2 text-xs font-medium text-muted-foreground">
                <div role="columnheader">Unit</div>
                <div role="columnheader">Phase</div>
                <div role="columnheader">Your checklist</div>
                <div role="columnheader">Tenant&apos;s checklist</div>
                <div role="columnheader" className="w-36"><span className="sr-only">Actions</span></div>
              </div>
              {rows.map((row) => (
                <div
                  key={rowKey(row)}
                  role="row"
                  data-testid={`checklist-row-${rowKey(row)}`}
                  className="grid grid-cols-[1.5fr_0.8fr_1fr_1.4fr_auto] items-center gap-4 border-b border-border px-4 py-3 text-sm last:border-b-0"
                >
                  <div role="cell" className="min-w-0">
                    <Link to={`/leases/${row.leaseId}#checklists`} className="inline-flex min-h-[44px] items-center font-medium text-foreground hover:underline lg:min-h-0">
                      {realUnitNumber(row.unitLabel) ?? row.propertyName}
                    </Link>
                    <div className="truncate text-xs text-muted-foreground">{row.propertyName}</div>
                  </div>
                  <div role="cell">{phaseLabel(row.phase)}</div>
                  <div role="cell">
                    <ChecklistStatusBadge state={row.landlord.state} side="landlord" />
                  </div>
                  <div role="cell">{tenantBadge(row)}</div>
                  <div role="cell" className="flex w-36 justify-end">{viewButton(row)}</div>
                </div>
              ))}
            </div>

            {/* Mobile / tablet: cards */}
            <ul className="m-0 flex list-none flex-col gap-3 p-0 lg:hidden" data-testid="checklists-cards">
              {rows.map((row) => (
                <li
                  key={rowKey(row)}
                  data-testid={`checklist-row-${rowKey(row)}-card`}
                  className="flex flex-col gap-3 rounded-xl border border-border bg-card p-[var(--card-padding)]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link to={`/leases/${row.leaseId}#checklists`} className="inline-flex min-h-[44px] items-center text-sm font-medium text-foreground hover:underline">
                        {realUnitNumber(row.unitLabel) ?? row.propertyName}
                      </Link>
                      <div className="truncate text-xs text-muted-foreground">
                        {row.propertyName} · {phaseLabel(row.phase)}
                      </div>
                    </div>
                    {row.leaseState === 'historical' && <Badge variant="danger">Archived</Badge>}
                  </div>
                  <dl className="m-0 flex flex-col gap-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">Your checklist</dt>
                      <dd className="m-0"><ChecklistStatusBadge state={row.landlord.state} side="landlord" /></dd>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">Tenant&apos;s checklist</dt>
                      <dd className="m-0">{tenantBadge(row)}</dd>
                    </div>
                  </dl>
                  {viewButton(row, 'w-full')}
                </li>
              ))}
            </ul>

            {checklists.hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  className="text-foreground"
                  onClick={checklists.loadMore}
                  disabled={checklists.isLoadingMore}
                  data-testid="checklists-load-more"
                >
                  {checklists.isLoadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {viewing && (
        <TenantChecklistModal walkthroughId={viewing.walkthroughId} phase={viewing.phase} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}
